import React, { useMemo } from "react";
import { Alert, Input, Radio, Space, Switch, Table, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  RESTORE_MODE,
  RESTORE_STRATEGY,
  STRATEGY_HELP,
} from "../constants";

const { Text } = Typography;

/**
 * The half of a restore dialog that is identical whether the rows come from a stored
 * archive or from another server: what to write, how to treat rows that already exist,
 * and which records specifically.
 *
 * `tables` entries: { name, rowCount, supportsIdFilter, disabled, disabledReason, extra }
 * `value`: { mode, strategy, tables: string[], ids: { [table]: string }, skipSafetyBackup }
 */
const RestoreOptions = ({ tables = [], value, onChange, loading }) => {
  const patch = (changes) => onChange({ ...value, ...changes });

  const selectable = useMemo(() => tables.filter((t) => !t.disabled), [tables]);

  const selectedNames = useMemo(
    () => (value.mode === RESTORE_MODE.FULL ? selectable.map((t) => t.name) : value.tables || []),
    [value.mode, value.tables, selectable]
  );

  const columns = [
    {
      title: "Table",
      dataIndex: "name",
      key: "name",
      render: (name, row) => (
        <Space size={6}>
          <Text strong={!row.disabled} type={row.disabled ? "secondary" : undefined}>
            {name}
          </Text>
          {row.disabled && row.disabledReason && (
            <Tooltip title={row.disabledReason}>
              <InfoCircleOutlined style={{ color: "#faad14" }} />
            </Tooltip>
          )}
          {row.extra}
        </Space>
      ),
    },
    {
      title: "Rows in source",
      dataIndex: "rowCount",
      key: "rowCount",
      width: 140,
      align: "right",
      render: (count) => (count === null || count === undefined ? "—" : count.toLocaleString()),
    },
    {
      title: (
        <Space size={6}>
          Restore only these IDs
          <Tooltip title="Leave empty to restore the whole table. Comma-separated primary-key values, e.g. 12, 18, 25. Only available for single-column keys.">
            <InfoCircleOutlined />
          </Tooltip>
        </Space>
      ),
      key: "ids",
      width: 320,
      render: (_, row) => {
        const active = selectedNames.includes(row.name);

        if (!row.supportsIdFilter) {
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.disabled ? "—" : "Composite or missing key — whole table only"}
            </Text>
          );
        }

        return (
          <Input
            size="small"
            allowClear
            placeholder="all rows"
            disabled={!active || row.disabled}
            value={value.ids?.[row.name] ?? ""}
            onChange={(e) => patch({ ids: { ...(value.ids || {}), [row.name]: e.target.value } })}
          />
        );
      },
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <div>
        <Text strong>Scope</Text>
        <div style={{ marginTop: 8 }}>
          <Radio.Group
            value={value.mode}
            onChange={(e) => patch({ mode: e.target.value })}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value={RESTORE_MODE.FULL}>Everything</Radio.Button>
            <Radio.Button value={RESTORE_MODE.SELECTIVE}>Selected tables</Radio.Button>
          </Radio.Group>
        </div>
      </div>

      <div>
        <Text strong>Existing rows</Text>
        <div style={{ marginTop: 8 }}>
          <Radio.Group
            value={value.strategy}
            onChange={(e) => patch({ strategy: e.target.value })}
          >
            <Space direction="vertical" size={4}>
              <Radio value={RESTORE_STRATEGY.UPSERT}>Update and add (recommended)</Radio>
              <Radio value={RESTORE_STRATEGY.INSERT_MISSING}>Only add what is missing</Radio>
              <Radio value={RESTORE_STRATEGY.REPLACE}>Empty the tables and refill</Radio>
            </Space>
          </Radio.Group>
        </div>
        <Alert
          style={{ marginTop: 8 }}
          type={value.strategy === RESTORE_STRATEGY.REPLACE ? "warning" : "info"}
          showIcon
          message={STRATEGY_HELP[value.strategy]}
        />
      </div>

      <div>
        <Text strong>Tables</Text>
        <Table
          style={{ marginTop: 8 }}
          size="small"
          rowKey="name"
          loading={loading}
          columns={columns}
          dataSource={tables}
          pagination={tables.length > 12 ? { pageSize: 12, size: "small" } : false}
          scroll={{ y: 320 }}
          rowSelection={
            value.mode === RESTORE_MODE.SELECTIVE
              ? {
                  selectedRowKeys: value.tables || [],
                  onChange: (keys) => patch({ tables: keys }),
                  getCheckboxProps: (row) => ({ disabled: row.disabled }),
                }
              : undefined
          }
        />
        {value.mode === RESTORE_MODE.FULL && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            All {selectable.length} matching tables will be restored.
          </Text>
        )}
      </div>

      <div>
        <Space size={10}>
          <Switch
            checked={!value.skipSafetyBackup}
            onChange={(checked) => patch({ skipSafetyBackup: !checked })}
          />
          <Text>Take a safety backup first</Text>
          <Tooltip title="Captures the affected tables into a Safety archive before anything is written, so this restore can be undone by restoring that archive.">
            <InfoCircleOutlined />
          </Tooltip>
        </Space>
        {value.skipSafetyBackup && (
          <Alert
            style={{ marginTop: 8 }}
            type="warning"
            showIcon
            message="Without a safety backup this restore cannot be undone."
          />
        )}
      </div>
    </Space>
  );
};

/** Turns the comma-separated ID inputs into the payload shape the API expects. */
export const buildIdsPayload = (value, selectedNames) => {
  const ids = {};

  Object.entries(value.ids || {}).forEach(([table, raw]) => {
    if (!selectedNames.includes(table)) return;

    const parsed = String(raw || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

    if (parsed.length > 0) ids[table] = parsed;
  });

  return Object.keys(ids).length > 0 ? ids : undefined;
};

export const DEFAULT_RESTORE_OPTIONS = {
  mode: RESTORE_MODE.FULL,
  strategy: RESTORE_STRATEGY.UPSERT,
  tables: [],
  ids: {},
  skipSafetyBackup: false,
};

export default RestoreOptions;
